/**
 * Loan drilldown header — consolidated "Settings" gear menu.
 *
 * A single gear button (next to the View dropdown) that houses every loan-level
 * settings action that used to live as separate title-row buttons:
 *   • Edit Correspondent      → UpdateLoanDataModal(correspondentCompanyId)
 *   • External Sync           → the Salesforce / Box sync flows (reused verbatim
 *                               from LoanSyncDropdownButton via its exported modals)
 *   • Move pipeline / Archive → the same endpoints LoanActionsMenu drove
 *   • Display                 → per-user view preferences for this screen
 *                               (currently: show/hide the tab-strip icons)
 *
 * Endpoints (eastview-os, pipeline-transfer.md + the archive/restore routes):
 *   POST /underwriting/archive_loan/{dealId}          { reason? }  — soft-archive
 *   POST /underwriting/move_to_acquisitions/{dealId}               — preclose → acq
 *   POST /loan_acquisitions/move_to_underwriting/{dealId}          — acq → preclose
 */
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { isNil } from "lodash";

import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListSubheader from "@mui/material/ListSubheader";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import HandshakeOutlined from "@mui/icons-material/HandshakeOutlined";
import SwapHorizOutlined from "@mui/icons-material/SwapHoriz";
import ArchiveOutlined from "@mui/icons-material/ArchiveOutlined";
import AddRounded from "@mui/icons-material/AddRounded";
import CloudSyncOutlined from "@mui/icons-material/CloudSyncOutlined";
import AssignmentOutlined from "@mui/icons-material/AssignmentOutlined";
import ViewCompactOutlined from "@mui/icons-material/ViewCompactOutlined";

import { losPipelineApi } from "../../../api/losPipelineApi";
import { useIsAcquisitions } from "../../../pipelines/PipelineContext";
import { useUnderwritingHook } from "../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";
import UpdateLoanDataModal from "../../../components/common/Loan/UpdateLoanDataModal";
import { useLoanNavIcons } from "./useLoanNavIcons";
import {
  SYNC_TYPES,
  CreateSalesforceOpportunityModal,
  SalesforceSyncConfirmationModal,
  BoxSyncConfirmationModal,
} from "../buttons/LoanSyncDropdownButton";

const subheaderSx = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#94a3b8",
  lineHeight: "28px",
  bgcolor: "transparent",
};
const itemSx = { fontSize: 13 };

const LoanSettingsMenu = () => {
  const loan = useSelector((state) => state.loanDrilldown);
  const dealId = loan?._id;
  const isAcquisitions = useIsAcquisitions();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getLoanById } = useUnderwritingHook();
  const [showNavIcons, setShowNavIcons] = useLoanNavIcons();

  const [anchor, setAnchor] = useState(null);
  // Which modal/dialog is open: "correspondent" | "archive" | a SYNC_TYPES value.
  const [active, setActive] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const close = () => setAnchor(null);
  const closeActive = () => setActive(null);

  // A loan is "linked" to Salesforce once it has an external sync id.
  const hasSalesforceLink = !isNil(loan?.sync_loan_id);

  const refreshLoan = useCallback(() => {
    getLoanById({
      loanId: dealId,
      onSuccessFn: (data) => dispatch(loanDrilldownSet(data)),
    });
  }, [getLoanById, dealId, dispatch]);

  const move = useCallback(async () => {
    if (!dealId || busy) return;
    close();
    setBusy(true);
    try {
      if (isAcquisitions) {
        await losPipelineApi.moveToUnderwriting(dealId);
        enqueueSnackbar("Loan moved to Underwriting (pre-close).", {
          variant: "success",
        });
        navigate(`/loan/${dealId}`);
      } else {
        await losPipelineApi.moveToAcquisitions(dealId);
        enqueueSnackbar("Loan moved to Acquisitions.", { variant: "success" });
        navigate(`/acquisition/${dealId}`);
      }
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.detail || err?.message || "Could not move the loan.",
        { variant: "error" },
      );
    } finally {
      setBusy(false);
    }
  }, [dealId, busy, isAcquisitions, enqueueSnackbar, navigate]);

  const archive = useCallback(async () => {
    if (!dealId || busy) return;
    setBusy(true);
    try {
      await losPipelineApi.archiveLoan(dealId, reason.trim() || undefined);
      enqueueSnackbar("Loan archived.", { variant: "success" });
      setActive(null);
      // The loan is no longer in the active pipeline — return to the list.
      navigate(isAcquisitions ? "/acquisitions" : "/loans");
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.detail ||
          err?.message ||
          "Could not archive the loan.",
        { variant: "error" },
      );
    } finally {
      setBusy(false);
    }
  }, [dealId, busy, reason, isAcquisitions, enqueueSnackbar, navigate]);

  if (!dealId) return null;

  const openActive = (key) => {
    close();
    setActive(key);
  };

  return (
    <>
      <Tooltip title="Settings">
        <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}>
          <SettingsOutlined fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 248, mt: 0.75 } } }}
      >
        <ListSubheader sx={subheaderSx} disableSticky>
          Loan
        </ListSubheader>
        <MenuItem sx={itemSx} onClick={() => openActive("correspondent")}>
          <ListItemIcon>
            <HandshakeOutlined fontSize="small" />
          </ListItemIcon>
          Edit Correspondent
        </MenuItem>

        <Divider />
        <ListSubheader sx={subheaderSx} disableSticky>
          External Sync
        </ListSubheader>
        <MenuItem
          sx={itemSx}
          disabled={hasSalesforceLink}
          onClick={() => openActive(SYNC_TYPES.CREATE_OPPORTUNITY)}
        >
          <ListItemIcon>
            <AddRounded fontSize="small" />
          </ListItemIcon>
          {hasSalesforceLink
            ? "Create Salesforce Opportunity (Already Linked)"
            : "Create Salesforce Opportunity"}
        </MenuItem>
        <MenuItem
          sx={itemSx}
          disabled={!hasSalesforceLink}
          onClick={() => openActive(SYNC_TYPES.SALESFORCE_SYNC)}
        >
          <ListItemIcon>
            <CloudSyncOutlined fontSize="small" />
          </ListItemIcon>
          {hasSalesforceLink
            ? "Push to Salesforce"
            : "Push to Salesforce (External ID Missing)"}
        </MenuItem>
        <MenuItem sx={itemSx} disabled>
          <ListItemIcon>
            <AssignmentOutlined fontSize="small" />
          </ListItemIcon>
          Sync With Box
        </MenuItem>

        <Divider />
        <ListSubheader sx={subheaderSx} disableSticky>
          Display
        </ListSubheader>
        {/* A view preference, not a loan action: it changes nothing on the
            server, so the menu stays open and the switch flips in place. The
            strip re-renders immediately (useLoanNavIcons notifies directly).
            Turning the icons off buys back ~20px per tab — on a laptop that is
            what lets the whole strip fit instead of scrolling. */}
        <MenuItem
          sx={itemSx}
          onClick={() => setShowNavIcons(!showNavIcons)}
          disableRipple
        >
          <ListItemIcon>
            <ViewCompactOutlined fontSize="small" />
          </ListItemIcon>
          Tab icons
          <Switch
            edge="end"
            size="small"
            checked={showNavIcons}
            tabIndex={-1}
            inputProps={{ "aria-label": "Show icons in the tab navigation" }}
            sx={{ ml: "auto", pointerEvents: "none" }}
          />
        </MenuItem>

        <Divider />
        <ListSubheader sx={subheaderSx} disableSticky>
          Pipeline
        </ListSubheader>
        <MenuItem sx={itemSx} onClick={move}>
          <ListItemIcon>
            <SwapHorizOutlined fontSize="small" />
          </ListItemIcon>
          {isAcquisitions ? "Move to Underwriting" : "Move to Acquisitions"}
        </MenuItem>
        <MenuItem
          sx={{ ...itemSx, color: "#b91c1c" }}
          onClick={() => {
            close();
            setReason("");
            setActive("archive");
          }}
        >
          <ListItemIcon>
            <ArchiveOutlined fontSize="small" sx={{ color: "#b91c1c" }} />
          </ListItemIcon>
          Archive loan
        </MenuItem>
      </Menu>

      {/* Edit Correspondent — reuses the shared update-loan modal. */}
      <UpdateLoanDataModal
        loan={active === "correspondent" ? loan : null}
        field={"correspondentCompanyId"}
        onClose={closeActive}
        onSuccessFn={(data) => {
          if (data) dispatch(loanDrilldownSet(data));
          else refreshLoan();
        }}
      />

      {/* External Sync — reused verbatim from LoanSyncDropdownButton. */}
      {active === SYNC_TYPES.CREATE_OPPORTUNITY && (
        <CreateSalesforceOpportunityModal onClose={closeActive} />
      )}
      {active === SYNC_TYPES.SALESFORCE_SYNC && (
        <SalesforceSyncConfirmationModal onClose={closeActive} />
      )}
      {active === SYNC_TYPES.BOX_SYNC && (
        <BoxSyncConfirmationModal onClose={closeActive} />
      )}

      {/* Archive confirm. */}
      <Dialog
        open={active === "archive"}
        onClose={() => (busy ? null : closeActive())}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600 }}>
          Archive this loan?
        </DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            label="Reason (optional)"
            fullWidth
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            InputProps={{ sx: { fontSize: 13 } }}
            InputLabelProps={{ sx: { fontSize: 13 } }}
            helperText="Removes the loan from the active pipeline. It can be restored from the Archived view."
            FormHelperTextProps={{ sx: { fontSize: 11 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={closeActive} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disableElevation
            onClick={archive}
            disabled={busy}
            startIcon={
              busy ? (
                <CircularProgress size={14} thickness={5} color="inherit" />
              ) : undefined
            }
          >
            {busy ? "Archiving…" : "Archive"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LoanSettingsMenu;
